import { ref } from 'vue';
import config from '../config/environment';
import logger from '$utils/logger';
import type { HotelConfig } from '../page/types';

/**
 * Interface para la respuesta de la API de hotel-config
 */
interface HotelConfigResponse {
    success: boolean;
    data: HotelConfig;
}

/**
 * Composable para cargar y gestionar la configuración del hotel/merchant
 *
 * Acepta tanto slug como domain como identificador.
 * El endpoint del Backend ya soporta ambos formatos:
 * - /api/hotel-config/hotel-slug
 * - /api/hotel-config/custom.domain.com
 */
export function useHotelConfig() {
    const hotelConfig = ref<HotelConfig | null>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    /**
     * Carga la configuración del hotel/merchant usando slug o domain
     *
     * @param identifier - Puede ser el slug (ej: 'hotel-viaggio-mazatlan')
     *                     o el domain (ej: 'reservas.viaggioresort.test')
     * @returns true si la carga fue exitosa, false en caso de error
     */
    const loadConfig = async (identifier: string): Promise<boolean> => {
        if (!identifier) {
            error.value = 'Identifier is required';
            logger.error('useHotelConfig: identifier is required');
            return false;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const baseUrl = config.apiBaseUrl;
            const url = `${baseUrl}/hotel-config/${identifier}`;

            logger.info(`useHotelConfig: Loading config for identifier: ${identifier}`);
            logger.info(`useHotelConfig: API URL: ${url}`);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Failed to fetch hotel configuration: ${response.status}`);
            }

            const data: HotelConfigResponse = await response.json();

            if (!data.success || !data.data) {
                throw new Error('Invalid hotel configuration response');
            }

            hotelConfig.value = data.data;
            logger.info(`useHotelConfig: Config loaded successfully for ${identifier}`, {
                name: hotelConfig.value.name,
                slug: hotelConfig.value.slug,
                templateName: hotelConfig.value.templateName,
                customDomain: hotelConfig.value.customDomain
            });

            // Aplicar colores del hotel al documento si están definidos
            if (hotelConfig.value.bgColor) {
                document.documentElement.style.setProperty('--hotel-bg-color', hotelConfig.value.bgColor);
            }
            if (hotelConfig.value.fgColor) {
                document.documentElement.style.setProperty('--hotel-fg-color', hotelConfig.value.fgColor);
            }

            return true;
        } catch (err: any) {
            error.value = err.message || 'Unknown error loading hotel config';
            logger.error('useHotelConfig: Error loading config:', err);
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    return {
        hotelConfig,
        isLoading,
        error,
        loadConfig
    };
}
